import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Observable, map, startWith } from 'rxjs';
import { ContractService } from 'src/app/contract/shared/contract.service';
import { UserService } from 'src/app/shared/user.service';

@Component({
  selector: 'app-clause-list',
  templateUrl: './clause-list.component.html',
  styleUrls: ['./clause-list.component.css']
})
export class ClauseListComponent implements OnInit, AfterViewInit {

  dataSource = new MatTableDataSource<any>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('contractInput') contractInput!: ElementRef<HTMLInputElement>;
  @ViewChild('templateInput') templateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;

  displayedColumns: string[] = ['Clause Name', 'Contract Type', 'Mandatory', 'Version', 'Created By', 'Last Updated', 'Action'];
  templates: any;
  contractTypes: any;
  filteredOptions: Observable<string[]>;
  templateOptions: Observable<string[]>;
  selectedContractType = new FormControl('');
  selectedTemplate = new FormControl({ value: "", disabled: true });
  isNotFound: boolean = false;
  templateNotFound: boolean = false;
  contractTypeId: number = null;
  templateId: number = null;
  isLoading: boolean = true;
  dataLoading: boolean = false;

  constructor(public contractService: ContractService, public userService: UserService, public toast: ToastrService, private router: Router) {
    this.getClauses();
  }

  async ngOnInit() {
    this.dataLoading = true;
    await this.contractService.getContractTypes().then((res: any) => {
      this.contractTypes = res;
    })

    if (this.contractTypes) {
      this.filteredOptions = this.selectedContractType.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      );
    }
    this.dataLoading = false;
  }

  private _filter(value: string): any[] {
    this.isNotFound = false;
    const filterValue = value.toLowerCase();
    const results = this.contractTypes.filter(option => option.name.toLowerCase().includes(filterValue));
    if (results.length === 0)
      this.isNotFound = true

    return results;
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onInputChange(value: string) {
    this.filteredOptions = this.selectedContractType.valueChanges.pipe(
      startWith(value),
      map(val => this._filter(val || ''))
    );

  }
  
  clearField() {
    this.isLoading = true;
    this.templates = null;
    this.selectedTemplate.disable();
    this.dataSource = new MatTableDataSource<any>();
    this.selectedContractType.setValue('');
    this.selectedTemplate.setValue('');
    this.templateInput.nativeElement.value = '';
    this.contractInput.nativeElement.value = '';
    this.filterInput.nativeElement.value = '';
    this.contractTypeId = null;
    this.templateId = null;
    this.getClauses();
  }

  async onContractTypeSelect(event) {
    this.isLoading = true;
    this.dataSource.data = []
    this.filterInput.nativeElement.value = '';
    this.selectedContractType.setValue(event.option.value.name);
    this.contractTypeId = event.option.value.contractTypeId;
    await this.contractService.getTemplates2(event.option.value.contractTypeId).then((res: any) => {
      if (res.length === 0) {
        this.toast.warning('There is no templates associated with the ' + event.option.value.name + ' contract type!', '');
        this.templates = null;
        this.selectedTemplate.disable();
      }
      else {
        this.templates = res;
        this.selectedTemplate.enable();
      }
      this.getClauses();
    });

    if (this.contractTypes) {
      this.filteredOptions = this.selectedContractType.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      );
    }

    this.selectedTemplate.setValue('');
    this.templateOptions = new Observable<string[]>();

    if (this.templates) {
      this.templateOptions = this.selectedTemplate.valueChanges.pipe(
        startWith(''),
        map(value => this._templateFilter(value || ''))
      );
    }
  }

  private _templateFilter(value: string): any[] {
    this.templateNotFound = false;
    const filterValue = value.toLowerCase();
    const results = this.templates.filter(option => option.templateName.toLowerCase().includes(filterValue));

    if (results.length === 0)
      this.templateNotFound = true

    return results;
  }

  onTemplateChange(value: string) {
    if (typeof value === 'string') {
      this.templateOptions = this.templates.valueChanges.pipe(
        startWith(value),
        map(val => this._templateFilter((val as string) || ''))
      );
    }
  }

  clearTemplateField() {
    this.isLoading = true;
    this.dataSource = new MatTableDataSource<any>();
    this.selectedTemplate.setValue('');
    this.templateInput.nativeElement.value = null;
    this.templateId = null;
    this.getClauses();
  }


  async onTemplateSelect(event) {
    this.isLoading = true;
    this.dataSource = new MatTableDataSource<any>();
    this.filterInput.nativeElement.value = '';
    this.selectedTemplate.setValue(event.option.value.templateName);
    this.templateId = event.option.value.templateId;
    if (this.templates) {
      this.templateOptions = this.selectedTemplate.valueChanges.pipe(
        startWith(''),
        map(value => this._templateFilter(value || ''))
      );
    }
    this.getClauses();
  }

  async getClauses() {
    await this.contractService.getAllClauses(this.contractTypeId, this.templateId).then((allClauses: any) => {
      allClauses.forEach((clauses: any) => {
        clauses.updatedDate = moment(clauses.updatedDate).format('lll')
      });
      this.dataSource.data = allClauses;
    });

    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.dataSource.sort.active = 'Last Updated';
      this.dataSource.sort.direction = 'desc';
      this.customSortData({
        active: 'Last Updated',
        direction: 'desc'
      } as Sort);
    }

    if (this.paginator) {
      this.paginator.firstPage();
    }

    this.isLoading = false;
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim();
    filterValue = filterValue.toLowerCase();
    this.dataSource.filter = filterValue;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  customSortData(event: Sort) {
    const data = this.dataSource.data.slice();
    if (!event.active || event.direction === '') {
      this.dataSource.data = data;
      return;
    }

    this.dataSource.data = data.sort((a, b) => {
      const valueA = (a[event.active] || '').toString().toLowerCase();
      const valueB = (b[event.active] || '').toString().toLowerCase();

      return this.compare(valueA, valueB, event.direction === 'asc');
    });

    // If pagination is present, reset to the first page after sorting
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }


  compare(a: string, b: string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

}
